import React, { useEffect } from 'react';
import { ReportDocument, printReportDocument, downloadReportCsv, emailReportSummary } from './ReportDriver';

/**
 * Full-screen overlay that presents a finished End of Period P&L report.
 *
 * Wraps the shared ReportDocument (branded header, metadata, stat cards,
 * category tables, and the Countertop footer logo) so the report shown through
 * the approval/review flow can be read, printed, downloaded, and emailed.
 */
const EopReportModal = ({ descriptor, onClose, onEdit }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!descriptor) return null;

  const reportTitle = descriptor?.report?.title || 'End of Period Report';
  const csvName = `${(reportTitle || 'eop_report').toLowerCase().replace(/[^a-z0-9]+/g, '_')}.csv`;

  return (
    <div className="erm-overlay" role="dialog" aria-modal="true" aria-label="End of Period report">
      <div className="erm-backdrop" onClick={onClose} />
      <div className="erm-panel">
        <div className="erm-bar">
          <span className="erm-bar-title">{reportTitle}</span>
          <div className="erm-bar-actions">
            {typeof onEdit === 'function' && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onEdit}>
                Edit Report
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="Close">
              Close
            </button>
          </div>
        </div>
        <div className="erm-scroll">
          <ReportDocument
            descriptor={descriptor}
            onPrint={() => printReportDocument(descriptor)}
            onDownload={() => downloadReportCsv(descriptor, csvName)}
            onEmail={() => emailReportSummary(descriptor, buildEmailLines(descriptor))}
          />
        </div>
      </div>

      <style>{ERM_CSS}</style>
    </div>
  );
};

function buildEmailLines(descriptor) {
  return (descriptor?.stats || []).map((s) => `${s.label}: ${s.value}`);
}

const ERM_CSS = `
  .erm-overlay {
    position: fixed;
    inset: 0;
    z-index: 1080;
    display: flex;
    align-items: flex-start;
    justify-content: center;
  }
  .erm-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(40, 28, 20, 0.55);
    backdrop-filter: blur(2px);
  }
  .erm-panel {
    position: relative;
    z-index: 1;
    margin: 2.5vh auto;
    width: min(960px, 96vw);
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    background: #f4efe9;
    border-radius: 14px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
    overflow: hidden;
  }
  .erm-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1rem;
    background: #fff;
    border-bottom: 1px solid #e7ddd2;
  }
  .erm-bar-title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    color: #3D2518;
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .erm-bar-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  .erm-scroll {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 1rem;
  }
  .erm-scroll .rd-shell { margin-bottom: 0 !important; }
  @media (max-width: 767px) {
    .erm-panel { margin: 0; width: 100vw; max-height: 100vh; border-radius: 0; }
  }
`;

export default EopReportModal;
